<template>
  <section>
    <header>Основное</header>
    <PriceDescription
      show-price-start
      :order-option="bid"
      entity-type="bid"
      :best-bid="bid.tender.bestBid"
      :disabled="bid.canChangeRequisites === false"
    />
  </section>
  <section class="section-qty">
    <VInputNumberRusV2
      v-model="bid.qtyAvailable"
      :type="Qty"
      class="qty"
      label="Кол-во"
      required
      :validate-method="$event => validateQty($event)"
      :disabled="bid.canChangeRequisites === false"
      :class="{ attention: bid.getQtyMismatch(bid.tender.qtyDesired) }"
    >
      <template #helper-text>
        <VHelperText
          theme="blue-flat"
        >
          Необходимое количество {{ bid.tender.qtyDesired.format(bid.unit) }}
        </VHelperText>

        <template v-if="bid.canChangeRequisites === false">
          <div class="qty-descriptor">
            Доступное к заказу: {{ bid.qtyLeft.format(bid.unit) }}
          </div>
          <div class="qty-descriptor">
            Уже заказано: {{ bid.qtyOrdered.format(bid.unit) }}
          </div>
        </template>
      </template>
    </VInputNumberRusV2>
  </section>
  <section>
    <VDateInput
      ref="expirationDate"
      v-model="bid.productExpirationDate"
      label="Партия годна до"
      :disabled="bid.canChangeRequisites === false"
      required
      :validate-method="$event => bid.validateExpirationDate($event)"
      :class="{ attention: bid.tender.product.hasExpirationRequirement && bid.getExpirationDateMismatch(bid.tender.product.requiredExpirationDate) }"
    >
      <template #helper-text>
        <VHelperText
          v-if="bid.tender.product.expirationDays"
          theme="blue-flat"
        >
          Минимальный срок годности {{ bid.tender.product.expirationDays.name }}
        </VHelperText>
      </template>
    </VDateInput>
  </section>

  <hr>

  <PaymentTermEditorCompact
    :disabled="bid.canChangeRequisites === false"
    :entity="bid"
  >
    <template #description>
      <dl
        class="cursor-pointer"
        @mouseenter="$core.dialogs.DeferredPaymentPopup.show({ bid, target: $event.target })"
        @mouseleave="$core.dialogs.DeferredPaymentPopup.hide()"
      >
        <dt>
          Кредитная наценка
          <VIcon
            theme="outlined"
            material="info"
          />
        </dt>
        <dd>
          {{ bid.priceModDeferredPayment }}
        </dd>
      </dl>
    </template>
  </PaymentTermEditorCompact>

  <hr>

  <section class="delivery">
    <header>Доставка</header>

    <VGroupButtonSelector
      v-model="bid.deliveryType"
      value-key="id"
      label-key="name"
      :options="deliveryTypeSellerOptions"
      :disabled="bid.canChangeRequisites === false"
    />

    <VFormItem
      class="shipment"
    >
      <VDateRangeInputNew
        required
        label="Интервал отгрузки"
        class="shipment-input"
        v-model="bid.shipmentPeriod"
        :disabled="bid.canChangeRequisites === false"
        :class="{ attention: getReceiptMismatch() }"
      >
        <template #helper-text>
          <div class="recommended">
            Желаемый интервал отгрузки {{ bid.getDesiredShipmentPeriod() || '--' }}
          </div>

          <VHelperText theme="blue-flat">
            <template v-if="bid.tender?.preorderShipmentPeriod?.isFilled() === true">
              Желаемый период поставки {{ bid.tender.preorderShipmentPeriod.toString() }}
            </template>
            <template
              v-else-if="$core.utils.isNullOrUndefined(bid.tender.desiredReceiptDate) === false">
              Желаемая дата поставки {{ $core.utils.date.format(bid.tender.desiredReceiptDate) }}
            </template>
          </VHelperText>

        </template>
      </VDateRangeInputNew>
    </VFormItem>

    <!-- Срок доставки — нативное число дней, не семантический тип. -->
    <VInputNumberRus
      ref="deliveryDaysInput"
      v-model="bid.deliveryDays"
      label="Срок доставки до покупателя"
      :precision="0"
      required
      :disabled="bid.canChangeRequisites === false || bid.deliveryType.id === 'byETP'"
      :min="0"
      :max="365"
    >
      <template #helper-text>
        <template v-if="bid.deliveryType.id === 'byETP'">
          Доставкой до покупателя занимается Платформа.
        </template>
      </template>
    </VInputNumberRus>

    <VSelectV2
      ref="sellerStoreControl"
      v-model="bid.sellerStore"
      class="store-select"
      label="Склад"
      hide-clear-btn
      :data="sellerStoreSource"
      required
      :validate-method="store => store?.validateTransportZone()"
      :disabled="bid.canChangeRequisites === false"
    >
      <template #helper-text>
        <template v-if="bid.deliveryType.id === 'byETP'">
          Выберите склад, откуда забирать груз
        </template>
        <template v-if="bid.deliveryType.id === 'bySeller'">
          Выберите склад, откуда будете отгружать груз
        </template>
      </template>
    </VSelectV2>

    <VSelectV2
      v-model="bid.transportationRoute"
      class="transport-route-select"
      label="Маршрут перевозки"
      hide-clear-btn
      :data="transportRouteSource"
      disabled
    />
  </section>

  <template v-if="bid.tender.isPreorderNeeded === true">
    <hr>

    <section class="preorder">
      <header>Отгрузка по предзаказу</header>

      <VHelperText
        theme="blue-flat"
      >
        Этим пунктом вы подтверждаете, что покупатель сможет зарезервировать у вас этот
        товар
        и
        вывозить по графику выбранными партиями к любой желаемой дате поставке. Оплата будет
        производиться частями каждый раз по факту доставки выбранной партии согласно периоду
        отсрочки
      </VHelperText>

      <VInputNumberRusV2
        ref="preorderMinQtyInput"
        v-model="bid.preorderMinQty"
        :type="Qty"
        label="Минимальное количество по предзаказу"
        required
        :validate-method="$event => bid.validatePreorderMinQty($event)"
        :disabled="bid.canChangeRequisites === false"
      >
        <template #helper-text>
          Минимальное количество товара, которое вы готовы поставить по предзаказу
        </template>
      </VInputNumberRusV2>

      <VInputNumberRusV2
        ref="preorderMonthlyReserveInput"
        v-model="bid.preorderMonthlyReserve"
        :type="Qty"
        :validate-method="$event => bid.validatePreorderMonthlyReserve($event)"
        label="Минимальный страховой запас"
        :disabled="bid.canChangeRequisites === false"
        :class="{ attention: bid.getPreorderReserveMismatch(bid.tender.preorderMonthlyReserve) }"
      >
        <template #helper-text>
          Количество товара, которое вы обязуетесь постоянно поддерживать на складе
          дополнительно
          (но не выше остатка по всему Предзаказу) к заказанным партиям покупателем для
          быстрой
          дополнительной отгрузки в течение 3-5 дней. Если вы готовы сразу хранить весь
          товар
          по
          Предзаказу, то укажите его
        </template>
      </VInputNumberRusV2>
    </section>
  </template>
</template>

<style lang="scss" src="./CreateTenderBidPrimary.scss"/>

<script lang="ts">
import type {UnwrapNestedRefs} from 'vue';
import type TenderBid from '@/domains/TenderBids/TenderBid';
import type DeliveryType from '@/domains/DeliveryTypes/DeliveryType';
import type DateSimple from '@/core/types/DateSimple';
import ProductsService from '@/domains/Products/ProductsService';
import StoresService from '@/domains/Stores/StoresService';
import TransportRoutesService from '@/domains/TransportRoutes/TransportRoutesService';
import Qty from '@/core/types/Qty';

import VHelperText from '@/lib/components/VHelperText/VHelperText.vue';
import PriceDescription
  from '@/domains/TenderBids/components/PriceDescription/PriceDescription.vue';
import PaymentTermEditorCompact
  from '@/domains/PaymentTerms/components/PaymentTermEditorCompact/PaymentTermEditorCompact.vue';

import VGroupButtonSelector from '@/components/VGroupButtonSelector/VGroupButtonSelector.vue';

export default defineComponent({
  name: 'CreateTenderBidPrimary',

  components: {
    VGroupButtonSelector,
    PaymentTermEditorCompact,
    VHelperText,
    PriceDescription,
  },

  inheritAttrs: true,

  props: {
    /** Развёрнутый тип: ставка приходит из reactive-состояния диалога,
     *  номинальный TenderBid туда уже не подходит. */
    bid: {
      type: Object as PropType<UnwrapNestedRefs<TenderBid>>,
      required: true,
    },
  },

  setup() {
    return {
      ProductsService,
      /** Класс нужен шаблону для :type у VInputNumberRusV2 */
      Qty,
    };
  },

  data() {
    return {
      /** Склады продавца. Грузятся при первом открытии списка — модель
       *  больше не тянет партнёра целиком ради stores. */
      sellerStoreSource: this.$core.loader.select<TenderBid>(({seller}) => StoresService.getList({
        partnerId: seller.id,
      }))
        .dependsOn({seller: () => this.bid.seller})
        .preloadOnFocus(),

      /**
       * Маршрут выводится из транспортных зон складов, поэтому грузится
       * источником: смена любого из складов сама перезагружает список.
       *
       * Маршрут всегда один — список из одного элемента, который VSelectV2
       * выберет сам. Пустой список означает, что маршрута нет.
       */
      transportRouteSource: this.$core.loader.select<TenderBid>(async ({
                                                                         sellerStore,
                                                                         buyerStore
                                                                       }) => {
        if (sellerStore.transportZone === null || buyerStore.transportZone === null) {
          return [];
        } else {
          const transportationRoute = await TransportRoutesService.getByZones(
            sellerStore.transportZone.id,
            buyerStore.transportZone.id,
          );

          if (transportationRoute !== null && transportationRoute.isDeleted === false) {
            return [transportationRoute];
          } else {
            return [];
          }
        }
      })
        .dependsOn({
          sellerStore: () => this.bid.sellerStore,
          buyerStore: () => this.bid.buyerStore,
        })
        .default((options: any[]) => {
          /** Пустой список — осознанное отсутствие значения. Возвращаем null,
           *  чтобы контрол эмитнул input: без него форма не узнает, что
           *  маршрута нет. */
          if (this.$core.utils.isEmptyArray(options) === true) {
            return null;
          } else {
            return options[0];
          }
        })
        .preloadOnMount(this.bid.isExisting === false),
    };
  },

  computed: {
    /** Продавец выбирает между вывозом Платформой и собственной доставкой. */
    deliveryTypeSellerOptions(): DeliveryType[] {
      return [
        this.$core.dictionaries.deliveryTypes.get('byETP'),
        this.$core.dictionaries.deliveryTypes.get('bySeller'),
      ];
    },

    /**
     * Крайняя дата отгрузки: от желаемой даты получения, указанной в тендере,
     * отнимаем срок доставки до покупателя.
     *
     * Желаемой даты может не быть — тогда считать нечего, и подсказку
     * в интервале отгрузки не показываем.
     */
    recommendedShipmentDate(): DateSimple | null {
      if (this.$core.utils.isNullOrUndefined(this.bid.tender.desiredReceiptDate) === true) {
        return null;
      } else {
        return this.bid.tender.desiredReceiptDate.subtract({days: this.bid.deliveryDays});
      }
    },
  },

  mounted() {
    /** При создании ставки нужно сразу провалидировать значение годности товара */
    this.$refs.expirationDate?.validateForce();

    /** И зону склада сразу: если её нет, форму заполнять бессмысленно —
     *  пусть поставщик сразу видит причину и меняет склад. */
    this.$refs.sellerStoreControl?.validateForce();
  },

  methods: {
    validateQty(value: Qty | null): string | null {
      ['preorderMinQtyInput', 'preorderMonthlyReserveInput'].forEach(key => {
        const input = this.$refs[key] as any;

        if (input && typeof input.validate === 'function') {
          input.validate();
        }
      });

      return this.$core.utils.number.validateDesiredQty(
        value,
        this.bid.packageVolume,
        undefined,
        this.bid.packageVolume,
        this.bid.unit,
      );
    },

    /**
     * Расхождение по периоду: предзаказу важен период отгрузки, обычному
     * тендеру — дата получения.
     */
    getReceiptMismatch(): string | null {
      if (this.bid.getDesiredShipmentPeriod() !== null) {
        return this.bid.getPreorderShipmentPeriodMismatch(this.bid.getDesiredShipmentPeriod());
      } else if (this.$core.utils.isNullOrUndefined(this.bid.tender.desiredReceiptDate) === false) {
        return this.bid.getReceiptDateMismatch(this.bid.tender.desiredReceiptDate);
      } else {
        return null;
      }
    },
  },
});
</script>
